import React from 'react';
import PropTypes from 'prop-types';
import * as immutable from 'immutable';
import {browserHistory} from 'react-router';
import connectToStores from 'alt-utils/lib/connectToStores';
import Loading from 'components/pure/utils/Loading';
import Button from '@material-ui/core/Button';
import AdminFilterWordProfileActions from 'actions/admin/AdminFilterWordProfileActions';
import TopicListItem from 'components/admin/topics/TopicListItem';
import AdminFilterWordProfilePageStore from 'stores/admin/AdminFilterWordProfilePageStore';
import AdminFilterWordProfilePageActions from 'actions/admin/AdminFilterWordProfilePageActions';
import AdminTopicEditorPageActions from 'actions/admin/AdminTopicEditorPageActions';
import AdminFilterWordActions from 'actions/admin/AdminFilterWordProfileActions';
import TopicMergeConfirmationDialog from 'components/admin/topics/TopicMergeConfirmationDialog';
import './TopicList.styl';

/**
 * Merge the selected topics into one.
 *
 * @param topicsToMerge {Immutable.List<NewFilterWord>} list of topics to merge.
 * @param mergedName {string} string name of the resulting merged topic
 */
function mergeSelectedTopics(topicsToMerge, mergedName) {
  AdminFilterWordProfileActions.mergeTopics([topicsToMerge, mergedName]).then(([id]) => {
    AdminFilterWordProfilePageActions.clearTopicsSelectedForMerge();
    AdminFilterWordActions.fetchAccountProfiles();
    AdminFilterWordProfilePageActions.setRecentMergedTopicId(id);

    setTimeout(() => AdminFilterWordProfilePageActions.clearRecentMergedTopicId(), 2000);
  });
}

/** List of topics for the right topics sidebar on the topics page. */
@connectToStores
export default class TopicList extends React.PureComponent {
  static propTypes = {
    editable: PropTypes.bool.isRequired,
    error: PropTypes.bool,
    loading: PropTypes.bool,
    topicsInAccount: PropTypes.instanceOf(immutable.List).isRequired,
  };

  static getStores() {
    return [AdminFilterWordProfilePageStore];
  }

  static getPropsFromStores() {
    const {topicsSelectedForMerge, recentMergedTopicId} =
      AdminFilterWordProfilePageStore.getState();

    return {
      topicsSelectedForMerge,
      recentMergedTopicId,
    };
  }

  constructor(props) {
    super(props);
    this.state = {
      mergeConfirmationDialogOpen: false,
    };
    this.openMergeConfirmationDialog = this.openMergeConfirmationDialog.bind(this);
    this.closeMergeConfirmationDialog = this.closeMergeConfirmationDialog.bind(this);
  }

  openMergeConfirmationDialog() {
    this.setState({
      mergeConfirmationDialogOpen: true,
    });
  }

  closeMergeConfirmationDialog() {
    this.setState({
      mergeConfirmationDialogOpen: false,
    });
  }

  /** Render the merge and create buttons. */
  renderButtons(topicsSelectedForMerge) {
    return (
      <div className="admin-topic-list-buttons">
        <Button
          variant="contained"
          color="primary"
          className="ev-button"
          onClick={() => {
            AdminTopicEditorPageActions.setupTopicEditorForNewTopic();
            browserHistory.push(`/admin/topics/new`);
          }}>
          Create Topic
        </Button>
        <Button
          variant="outlined"
          className="ev-button"
          disabled={topicsSelectedForMerge.size < 2}
          onClick={this.openMergeConfirmationDialog}>
          Merge Selected Topics
        </Button>
      </div>
    );
  }

  componentWillUnmount() {
    AdminFilterWordProfilePageActions.clearRecentMergedTopicId();
  }

  render() {
    const {editable, error, loading, topicsInAccount, topicsSelectedForMerge, recentMergedTopicId} =
      this.props;

    const content = loading ? (
      <Loading />
    ) : error ? (
      <p>There was an error loading the data.</p>
    ) : (
      topicsInAccount.map((topic, idx) => (
        <TopicListItem
          key={idx}
          editable={editable}
          topic={topic}
          topicsSelectedForMerge={topicsSelectedForMerge}
          highlighted={recentMergedTopicId === topic.filterWordId}
        />
      ))
    );

    const {mergeConfirmationDialogOpen} = this.state;
    const buttons = this.renderButtons(topicsSelectedForMerge);

    return (
      <div className="admin-topics-wrapper">
        <div className="topics-list">
          <h2>Available Topics</h2>
          {content}
        </div>
        <div className="topics-actions">
          <div className="topics-header">
            <span>Topic Actions</span>
          </div>
          {buttons}
        </div>
        <TopicMergeConfirmationDialog
          open={mergeConfirmationDialogOpen}
          onClose={this.closeMergeConfirmationDialog}
          onConfirm={mergedName => {
            mergeSelectedTopics(topicsSelectedForMerge, mergedName);
            this.closeMergeConfirmationDialog();
          }}
          topicsSelectedForMerge={topicsSelectedForMerge}
        />
      </div>
    );
  }
}
